import {
  Box,
  VStack,
  HStack,
  Text,
  Badge,
  Divider,
  Spinner,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  Radio,
  RadioGroup,
  Stack,
} from "@chakra-ui/react";
import React, { useState, useMemo } from "react";

const StreakLeaderboard = ({ 
  users, 
  users_subject, 
  users_score, 
  users_subject_score,
  users_score_this_month,
  users_subject_score_this_month,
  userData, 
  isLoading 
}) => {
  const [activeTab, setActiveTab] = useState(0);
  const [scorePeriod, setScorePeriod] = useState("overall");

  // Convert score objects to arrays and sort by score (descending) - Overall
  const usersScoreArray = useMemo(() => {
    if (!users_score) return [];
    return Object.values(users_score)
      .sort((a, b) => (b.score || 0) - (a.score || 0))
      .slice(0, 10);
  }, [users_score]);

  const usersSubjectScoreArray = useMemo(() => {
    if (!users_subject_score) return [];
    // Handle both array and object formats
    const scoreArray = Array.isArray(users_subject_score) 
      ? users_subject_score 
      : Object.values(users_subject_score);
    return scoreArray
      .sort((a, b) => (b.score || 0) - (a.score || 0))
      .slice(0, 10);
  }, [users_subject_score]);

  // Convert score objects to arrays and sort by score (descending) - This Month
  const usersScoreThisMonthArray = useMemo(() => {
    if (!users_score_this_month) return [];
    return Object.values(users_score_this_month)
      .sort((a, b) => (b.score || 0) - (a.score || 0))
      .slice(0, 10);
  }, [users_score_this_month]);

  const usersSubjectScoreThisMonthArray = useMemo(() => {
    if (!users_subject_score_this_month) return [];
    // Handle both array and object formats
    const scoreArray = Array.isArray(users_subject_score_this_month) 
      ? users_subject_score_this_month 
      : Object.values(users_subject_score_this_month);
    return scoreArray
      .sort((a, b) => (b.score || 0) - (a.score || 0))
      .slice(0, 10);
  }, [users_subject_score_this_month]);

  const LeaderboardItem = ({ user, rank, isCurrentUser = false, displayType = "streak" }) => {
    const getRankColor = () => {
      if (rank === 1) return "gold";
      if (rank === 2) return "silver";
      if (rank === 3) return "#CD7F32"; // Bronze
      return "gray.500";
    };

    const getRankIcon = () => {
      if (rank === 1) return "🥇";
      if (rank === 2) return "🥈";
      if (rank === 3) return "🥉";
      return null;
    };

    return (
      <Box
        w="100%"
        p={3}
        borderRadius="8px"
        bg={isCurrentUser ? "orange.50" : "white"}
        border={isCurrentUser ? "2px solid" : "1px solid"}
        borderColor={isCurrentUser ? "orange.300" : "gray.200"}
        boxShadow={isCurrentUser ? "0 2px 8px rgba(255,107,53,0.2)" : "0 1px 3px rgba(0,0,0,0.1)"}
      >
        <HStack spacing={3}>
          {/* Rank */}
          <Box
            w="40px"
            h="40px"
            borderRadius="50%"
            bg={rank <= 3 ? getRankColor() : "gray.200"}
            display="flex"
            alignItems="center"
            justifyContent="center"
            fontWeight="bold"
            color={rank <= 3 ? "white" : "gray.700"}
            fontSize="lg"
            flexShrink={0}
          >
            {getRankIcon() || rank}
          </Box>

          {/* Name and Info */}
          <VStack align="start" spacing={0} flex={1} minW={0}>
            <HStack spacing={2} w="100%">
              <Text
                fontWeight={isCurrentUser ? "bold" : "semibold"}
                fontSize="md"
                color={isCurrentUser ? "orange.700" : "gray.800"}
                isTruncated
              >
                {user?.user_name || "Unknown"}
              </Text>
              {isCurrentUser && (
                <Badge colorScheme="orange" fontSize="xs">You</Badge>
              )}
            </HStack>
          </VStack>

          {/* Streak Count or Score */}
          <VStack align="end" spacing={0} flexShrink={0}>
            <HStack spacing={1}>
              <Text fontSize="xl">{displayType === "streak" ? "🔥" : "⭐"}</Text>
              <Text
                fontWeight="bold"
                fontSize="lg"
                color={isCurrentUser ? "orange.600" : "gray.700"}
              >
                {displayType === "streak" ? (user?.streak_count || 0) : (user?.score || 0)}
              </Text>
            </HStack>
            <Text fontSize="xs" color="gray.500">
              {displayType === "streak" ? "days" : "points"}
            </Text>
          </VStack>
        </HStack>
      </Box>
    );
  };

  if (isLoading) {
    return (
      <VStack spacing={4} py={8}>
        <Spinner size="xl" color="orange.500" />
        <Text color="gray.600">Loading leaderboard...</Text>
      </VStack>
    );
  }

  const renderLeaderboard = (globalUsers, subjectUsers, displayType) => (
    <VStack spacing={6} align="stretch" w="100%">
      {/* Global Leaderboard */}
      <Box>
        <Text fontWeight="bold" fontSize="lg" mb={3} color="gray.800">
          🌍 Global Top Students
        </Text>
        <VStack spacing={2} align="stretch">
          {globalUsers && globalUsers.length > 0 ? (
            globalUsers.map((user, index) => (
              <LeaderboardItem
                key={user.user_name || user.user_id || user.id || index}
                user={user}
                rank={index + 1}
                isCurrentUser={
                  user.user_name === userData?.user_name || 
                  user.user_id === userData?.id || 
                  user.id === userData?.id
                }
                displayType={displayType}
              />
            ))
          ) : (
            <Box p={4} textAlign="center" color="gray.500">
              <Text>No rankings yet</Text>
            </Box>
          )}
        </VStack>
      </Box>

      <Divider />

      {/* Subject Leaderboard */}
      <Box>
        <Text fontWeight="bold" fontSize="lg" mb={3} color="gray.800">
          📚 Subject Top Students
        </Text>
        <VStack spacing={2} align="stretch">
          {subjectUsers && subjectUsers.length > 0 ? (
            subjectUsers.map((user, index) => (
              <LeaderboardItem
                key={user.user_name || user.user_id || user.id || index}
                user={user}
                rank={index + 1}
                isCurrentUser={
                  user.user_name === userData?.user_name || 
                  user.user_id === userData?.id || 
                  user.id === userData?.id
                }
                displayType={displayType}
              />
            ))
          ) : (
            <Box p={4} textAlign="center" color="gray.500">
              <Text>No rankings yet</Text>
            </Box>
          )}
        </VStack>
      </Box>
    </VStack>
  );

  return (
    <Tabs index={activeTab} onChange={setActiveTab} w="100%">
      <TabList>
        <Tab>🔥 Streak</Tab>
        <Tab>⭐ Score</Tab>
      </TabList>

      <TabPanels>
        <TabPanel px={0}>
          {renderLeaderboard(users, users_subject, "streak")}
        </TabPanel>
        <TabPanel px={0}>
          <VStack spacing={4} align="stretch" w="100%">
            {/* Score Period Selector */}
            <Box mb={2}>
              <RadioGroup onChange={setScorePeriod} value={scorePeriod}>
                <Stack direction="row" spacing={6}>
                  <Radio value="overall" size="md" colorScheme="orange">
                    <Text fontSize="sm" color="gray.700" fontWeight="medium">
                      Overall
                    </Text>
                  </Radio>
                  <Radio value="thisMonth" size="md" colorScheme="orange">
                    <Text fontSize="sm" color="gray.700" fontWeight="medium">
                      This Month
                    </Text>
                  </Radio>
                </Stack>
              </RadioGroup>
            </Box>

            {/* Leaderboard based on selected period */}
            {scorePeriod === "overall" 
              ? renderLeaderboard(usersScoreArray, usersSubjectScoreArray, "score")
              : renderLeaderboard(usersScoreThisMonthArray, usersSubjectScoreThisMonthArray, "score")
            }
          </VStack>
        </TabPanel>
      </TabPanels>
    </Tabs>
  );
};

export default StreakLeaderboard;

